import { __awaiter } from 'tslib';
import * as Long from 'long';
import md5 from 'js-md5';
import { im_proto } from '../../proto/index.js';
import { SdkConsts, WebSocketLevel, ErrorType, IMEvent } from '../consts.js';
import { BaseError } from '../errors/BaseError.js';
import { Subject } from '../events/Subject.js';
import { Logger } from '../metrics/logger.js';
import { decodeBlock } from '../utils/lz4.js';
import { serialize, serializeFrame, deserializeFrame, deserialize } from '../utils/serializer.js';
import { Monitor, MonitorItem } from '../metrics/monitor.js';
import { NetworkItem } from './NetworkItem.js';
import { AdapterManager } from '../../adapter/manager/AdapterManager.js';
import { AdapterType } from '../../adapter/manager/types.js';
import { ContextClassBase, ServiceIdentity } from '../service/RuntimeContext.js';
import { Ticker } from '../utils/ticker.js';
import { ConnectionStatus } from '../../adapter/abstract/IMNetApi.js';

class NetworkManager extends ContextClassBase {
    constructor(ctx) {
        super(ctx);
        // heartbeat and ws checker
        this.wsLastReceiveTime = 0;
        this.reqMap = new Map();
        this.manuallyClosed = false;
        const { webSocketLevel, heartbeatInterval, maxHeartbeatEmptyWindow } = this.ctx.option;
        this.heartbeatInterval = heartbeatInterval !== null && heartbeatInterval !== void 0 ? heartbeatInterval : SdkConsts.heartbeatInterval;
        this.maxEmptyWindow = maxHeartbeatEmptyWindow !== null && maxHeartbeatEmptyWindow !== void 0 ? maxHeartbeatEmptyWindow : SdkConsts.maxHeartbeatEmptyWindow;
        this.net = new (AdapterManager.getConstructor(ctx, AdapterType.NetApi))(this.ctx);
        this.http = new (AdapterManager.getConstructor(ctx, AdapterType.HttpClient))(this.ctx);
        if (webSocketLevel !== WebSocketLevel.Disable) {
            this.ws = new (AdapterManager.getConstructor(ctx, AdapterType.WebSocketClient))(this.ctx);
            this.prepareWs();
        }
        this.onMessage = new Subject(ctx);
    }
    // please update this if add new commands
    inSignCommandList(cmd) {
        return [
            im_proto.IMCMD.SEND_MESSAGE,
            im_proto.IMCMD.CREATE_CONVERSATION_V2,
            im_proto.IMCMD.CALL_VOIP,
            im_proto.IMCMD.ADD_CONVERSATION_PARTICIPANTS,
            im_proto.IMCMD.REMOVE_CONVERSATION_PARTICIPANTS,
            im_proto.IMCMD.UPDATE_CONVERSATION_PARTICIPANT,
            im_proto.IMCMD.SET_CONVERSATION_SETTING_INFO,
            im_proto.IMCMD.SET_CONVERSATION_CORE_INFO,
            im_proto.IMCMD.UPSERT_CONVERSATION_CORE_EXT_INFO,
            im_proto.IMCMD.UPSERT_CONVERSATION_SETTING_EXT_INFO,
            im_proto.IMCMD.DISSOLVE_CONVERSATION,
            im_proto.IMCMD.MARK_MESSAGE,
            im_proto.IMCMD.BATCH_UNMARK_MESSAGE,
            im_proto.IMCMD.SET_MESSAGE_PROPERTY,
            im_proto.IMCMD.MODIFY_MESSAGE_EXT // ???
            // Audit:
            //  AckConversationApply
            //  GetApplyUserExtraInfo
            //  SendConversationApply
            //  UpdateConversationAuditSwitch
            // Preview:
            //  CheckValidPreviewer
        ].includes(cmd);
    }
    frontierSign(payload) {
        // @ts-ignore
        const payloadMd5 = md5(payload);
        const wsInfo = {
            'X-MS-STUB': payloadMd5
        };
        // @ts-ignore
        if (typeof window !== 'undefined' &&
            typeof window.byted_acrawler !== 'undefined' &&
            typeof window.byted_acrawler.frontierSign === 'function') {
            // @ts-ignore
            const sig = window.byted_acrawler.frontierSign(wsInfo);
            return sig !== null && sig !== void 0 ? sig : {};
        }
        return {};
    }
    sendByBeacon(item) {
        return __awaiter(this, void 0, void 0, function* () {
            item.method = 'beacon';
            Logger.ctxDebug(this.ctx, `Beacon Request SeqId -> ${item.seqId}`, item.request);
            this.http.sendBeacon(item.url, item.request);
            return item;
        });
    }
    sendByHttp(item) {
        return __awaiter(this, void 0, void 0, function* () {
            item.method = 'http';
            Logger.ctxDebug(this.ctx, `Http Request SeqId -> ${item.seqId}`, item.request);
            try {
                this.reqMap.set(item.seqId, item);
                const res = yield this.http.sendRequest(item.url, item.request);
                item.response = res;
                if (res.status_code !== 0) {
                    Logger.ctxError(this.ctx, `Http Response SeqId -> ${item.seqId}`, item.response);
                }
                else {
                    Logger.ctxDebug(this.ctx, `Http Response SeqId -> ${item.seqId}`, item.response);
                }
                return item;
            }
            catch (networkError) {
                throw new BaseError({
                    ctx: this.ctx,
                    msg: 'network error',
                    type: ErrorType.NetworkError,
                    innerError: networkError,
                    allowRetry: true,
                    sender: this,
                    ignoreEvent: true
                });
            }
            finally {
                this.reqMap.delete(item.seqId);
            }
        });
    }
    // TODO: 大包 fallback
    sendByWs(item) {
        return __awaiter(this, void 0, void 0, function* () {
            item.method = 'ws';
            const u = serialize(item.request);
            const signHeader = this.inSignCommandList(item.cmd) ? this.frontierSign(u) : {};
            const newHeader = Object.assign(Object.assign({}, signHeader), this.ctx.option.headers);
            const frame = im_proto.Frame.create({
                service: this.ctx.option.service,
                method: this.ctx.option.method,
                headers: Object.entries(newHeader).map(([k, v]) => ({
                    key: k,
                    value: v
                })),
                seqid: Long.fromNumber(item.seqId),
                logid: Long.fromNumber(Date.now()),
                payload_type: 'pb',
                payload: u
            });
            item.frame = frame;
            try {
                this.reqMap.set(item.seqId, item);
                Logger.ctxDebug(this.ctx, `WS Request SeqId -> ${item.seqId}`, item.request);
                this.ws.send(serializeFrame(frame));
            }
            catch (networkError) {
                // 如果是 network error, 那么应该重连 ws
                this.reqMap.delete(item.seqId);
                throw new BaseError({
                    ctx: this.ctx,
                    msg: 'network error',
                    type: ErrorType.NetworkError,
                    innerError: networkError,
                    allowRetry: true,
                    sender: this
                });
            }
            return new Promise((resolve, reject) => {
                item.wsPromise = { resolve, reject };
            });
        });
    }
    receiveRaw(data) {
        this.resetWsHeartbeat();
        if (data.toString() === 'hi') {
            return;
        }
        try {
            const frame = deserializeFrame(this.ctx, data);
            if (frame.service !== this.ctx.option.service && !this.ctx.option.acceptIncorrectFrame) {
                // 长链需要丢弃 service id 不正确的消息
                Logger.ctxDebug(this.ctx, `WS Response dropped, local serviceId=${this.ctx.option.service}, message=${frame.service}`);
                this.resolve(ServiceIdentity.EventBus).emit(IMEvent.WebSocketReceiveUnexpectedFrame, this, frame);
                return;
            }
            let { payload } = frame;
            if (payload === undefined || payload === null) {
                Logger.ctxWarn(this.ctx, 'payload undefined or null');
                return;
            }
            const encodeType = frame.payload_encoding;
            if (encodeType === '__lz4') {
                let decompressed = new Uint8Array(payload.length * 10);
                let outputSize = 0;
                outputSize = decodeBlock(payload, decompressed);
                if (outputSize < 0 || outputSize > decompressed.length || outputSize < payload.length) {
                    Logger.ctxDebug(this.ctx, 'WS response with lz4 compress, but first time decompressed failed, try second time');
                    decompressed = new Uint8Array(1000 * 1024);
                    outputSize = decodeBlock(payload, decompressed);
                    if (outputSize < 0 || outputSize > decompressed.length || outputSize < payload.length) {
                        Logger.ctxWarn(this.ctx, 'WS response with lz4 compress, try second time fail.');
                        return;
                    }
                }
                payload = decompressed.slice(0, outputSize);
            }
            try {
                const resp = deserialize(this.ctx, payload);
                const res = im_proto.Response.create(resp);
                // drop packet which inbox type is not accpetable
                if (!this.resolve(ServiceIdentity.InboxType).isValidInbox(res.inbox_type) && res.sequence_id.eq(0)) {
                    Logger.ctxWarn(this.ctx, `WS Push dropped, local inboxType=${this.ctx.option.inboxType}, message=${res.inbox_type}`, 'resp:', res);
                    return;
                }
                const seqId = res.sequence_id.toNumber();
                let item = this.reqMap.get(seqId);
                if (item) {
                    if (res.status_code !== 0) {
                        Logger.ctxError(this.ctx, `WS Response SeqId -> ${res.sequence_id}`, res);
                    }
                    else {
                        Logger.ctxDebug(this.ctx, `WS Response SeqId -> ${res.sequence_id}`, res);
                    }
                    item.response = res;
                    item.wsPromise.resolve(item);
                    this.reqMap.delete(seqId);
                }
                else if (res.sequence_id.eq(0)) {
                    Logger.ctxDebug(this.ctx, 'WS Push', res);
                    item = new NetworkItem(this.ctx, res.cmd);
                    item.frame = frame;
                    item.response = res;
                    item.seqId = res.sequence_id.toNumber();
                    this.receive(item);
                }
                else {
                    // 不正常的包, 丢弃
                    Logger.ctxWarn(this.ctx, 'WS Response dropped, no handler', res);
                }
            }
            catch (hexdumpResp) {
                Logger.ctxWarn(this.ctx, 'WS Response dropped, IM Response deserialize fail, hex dump:', hexdumpResp, 'frame:', frame);
            }
        }
        catch (hexdumpFrame) {
            Logger.ctxWarn(this.ctx, 'WS Response dropped, Frame deserialize fail, hex dump: ', hexdumpFrame);
        }
    }
    send(item, options) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        return __awaiter(this, void 0, void 0, function* () {
            if (options.useBeacon) {
                yield this.sendByBeacon(item);
                item.response = im_proto.Response.create({});
                return item;
            }
            // 长链的默认重试次数始终是 3
            let maxWs = (_a = options.maxWsRetryTimes) !== null && _a !== void 0 ? _a : 3;
            // 如果长链重试的次数是 0, 那么 http 应该配置 3 次重试
            // 如果不是, 那么 http 默认配置 1 次
            let maxHttp = maxWs === 0 ? (_b = options.maxHttpRetryTimes) !== null && _b !== void 0 ? _b : 3 : ((_c = options.maxHttpRetryTimes) !== null && _c !== void 0 ? _c : (_d = this.ws) === null || _d === void 0 ? void 0 : _d.isOpen()) ? 1 : 3;
            if (maxHttp === 0) {
                // 至少应该走 1 次短链保底
                maxHttp = 1;
            }
            // if ws didn't receive packet for a long time, fallback to force http
            const receiveTimeRange = Monitor.performanceNow() - this.wsLastReceiveTime - this.heartbeatInterval * 1.5;
            if (receiveTimeRange > this.maxEmptyWindow && ((_e = this.ws) === null || _e === void 0 ? void 0 : _e.isOpen())) {
                maxWs = 0;
                maxHttp = (_f = options.maxHttpRetryTimes) !== null && _f !== void 0 ? _f : 3;
                Logger.ctxWarn(this.ctx, 'ws idle too long, fallback to http');
            }
            let wsRetryTimes = 0;
            let httpRetryTimes = 0;
            const wrapPromise = () => {
                let resolved = false;
                const racePromise = new Promise((_, reject) => {
                    // 永远不会 resolve, 因此无法推断类型
                    setTimeout(() => {
                        if (!resolved) {
                            this.reqMap.delete(item.seqId);
                            reject(new BaseError({
                                ctx: this.ctx,
                                msg: 'request timeout',
                                type: ErrorType.NetworkError,
                                allowRetry: true,
                                ignoreEvent: true,
                                sender: this
                            }));
                        }
                    }, this.ctx.option.timeout);
                });
                const reqPromise = () => __awaiter(this, void 0, void 0, function* () {
                    var _a;
                    // 重试策略
                    // 先检查下是否可以用长链发
                    // 条件是长链开启, 且仍然有重试次数
                    let useWs = false;
                    if (wsRetryTimes < maxWs && ((_a = this.ws) === null || _a === void 0 ? void 0 : _a.isOpen())) {
                        useWs = true;
                    }
                    // 长链重试次数已经超标了, 且重试了超过 1 次, 此时要尝试走短链发送
                    if (maxWs !== 0 && wsRetryTimes >= maxWs && httpRetryTimes === 0) {
                        if (this.ws !== undefined) {
                            Logger.ctxWarn(this.ctx, 'fallback to http');
                            useWs = false;
                        }
                    }
                    // 然后检查短链的保底次数
                    // 短链一定是最后使用的手段, 如果超出次数可以直接 fail 掉
                    if (httpRetryTimes >= maxHttp) {
                        throw new BaseError({
                            ctx: this.ctx,
                            msg: 'http retry times limit exceeded',
                            type: ErrorType.NetworkError,
                            sender: this,
                            args: {
                                cmd: item.cmd,
                                seqId: item.seqId
                            }
                        });
                    }
                    let pr;
                    if (useWs) {
                        wsRetryTimes++;
                        pr = this.sendByWs(item);
                    }
                    else {
                        httpRetryTimes++;
                        pr = this.sendByHttp(item);
                    }
                    try {
                        const reqItem = yield pr;
                        resolved = true;
                        this.reqMap.delete(item.seqId);
                        const { request: req, response: res } = reqItem;
                        this.resolve(ServiceIdentity.Monitor).emitNetwork(req, res);
                        // demotion
                        if (res.status_code === ErrorType.ResourceExhausted) {
                            throw new BaseError({
                                ctx: this.ctx,
                                msg: 'demotion enabled',
                                type: ErrorType.ResourceExhausted,
                                allowRetry: false,
                                sender: this,
                                logid: res === null || res === void 0 ? void 0 : res.log_id
                            });
                        }
                        // internal timeout
                        if ((res.status_code === ErrorType.InternalError &&
                            (res.error_desc.includes('i/o timeout') ||
                                res.error_desc.includes('rpc timeout') ||
                                res.error_desc.includes('RPC timeout'))) ||
                            res.error_desc.includes('connection timeout') ||
                            res.error_desc.includes('request timeout')) {
                            // 特判server超时
                            throw new BaseError({
                                ctx: this.ctx,
                                msg: 'request timeout',
                                type: ErrorType.NetworkError,
                                allowRetry: true,
                                ignoreEvent: true,
                                sender: this,
                                logid: res === null || res === void 0 ? void 0 : res.log_id
                            });
                        }
                        reqItem.retryTimes = wsRetryTimes + httpRetryTimes;
                        return reqItem;
                    }
                    catch (err) {
                        if (err.type) {
                            throw err;
                        }
                        else {
                            throw new BaseError({
                                ctx: this.ctx,
                                msg: 'request error',
                                type: ErrorType.NetworkError,
                                allowRetry: err.allowRetry,
                                innerError: err,
                                sender: this
                            });
                        }
                    }
                    finally {
                        this.reqMap.delete(item.seqId);
                    }
                });
                return Promise.race([racePromise, reqPromise()]);
            };
            let resp;
            while (true) {
                item.retryTimes++;
                if (item.retryTimes >= 10) {
                    item.error = new BaseError({
                        ctx: this.ctx,
                        msg: 'too much retry',
                        type: ErrorType.NetworkError,
                        sender: this
                    });
                    break;
                }
                try {
                    item.startTime = Date.now();
                    resp = yield wrapPromise();
                    item.endTime = Date.now();
                    if (typeof ((_h = (_g = resp === null || resp === void 0 ? void 0 : resp.response) === null || _g === void 0 ? void 0 : _g.server_execution_end_time) === null || _h === void 0 ? void 0 : _h.toNumber) === 'function' &&
                        typeof ((_k = (_j = resp === null || resp === void 0 ? void 0 : resp.response) === null || _j === void 0 ? void 0 : _j.request_arrived_time) === null || _k === void 0 ? void 0 : _k.toNumber) === 'function') {
                        Monitor.putTimeDelta(item.startTime, item.endTime, resp.response.server_execution_end_time.toNumber(), resp.response.request_arrived_time.toNumber());
                    }
                    if (resp !== undefined) {
                        break;
                    }
                }
                catch (e) {
                    item.error = e;
                    if ((e === null || e === void 0 ? void 0 : e.allowRetry) === true) {
                        let sleepTime = 500 + item.retryTimes * 100 + Math.random() * 100;
                        if (sleepTime > 2000) {
                            sleepTime = 2000;
                        }
                        Logger.ctxDebug(this.ctx, `req ${item.seqId} failed, retrying @ attempt ${item.retryTimes}, inner err:`, e);
                        yield new Promise(r => setTimeout(r, sleepTime));
                        continue;
                    }
                    else {
                        break;
                    }
                }
            }
            if (resp === undefined) {
                if (item.error === undefined) {
                    item.error = new BaseError({
                        ctx: this.ctx,
                        msg: 'unknown error',
                        type: ErrorType.NetworkError,
                        sender: this
                    });
                }
                throw item.error;
            }
            return resp;
        });
    }
    sendByHttpTo(url, method, req) {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof fetch !== 'undefined') {
                return (yield fetch(url)).arrayBuffer();
            }
            return this.http.send(url, method, req);
        });
    }
    receive(item) {
        this.resolve(ServiceIdentity.Monitor).emitNetwork(null, item.response);
        this.onMessage.next(item.response, this);
    }
    onWsClose() {
        return __awaiter(this, void 0, void 0, function* () {
            Logger.ctxDebug(this.ctx, 'ws closed');
            this.resolve(ServiceIdentity.EventBus).emit(IMEvent.WebSocketDisconnected, this, this.ws);
        });
    }
    prepareWs() {
        if (this.ctx.option.webSocketLevel === WebSocketLevel.Disable) {
            return;
        }
        this.ws.onMessage.subscribe(data => this.receiveRaw(data));
        this.ws.onClose.subscribe(() => this.onWsClose());
        this.ws.onError.subscribe(() => this.onWsClose());
        this.ws.onOpen.subscribe(() => {
            Logger.ctxDebug(this.ctx, 'ws connected');
            this.resolve(ServiceIdentity.EventBus).emit(IMEvent.WebSocketConnected, this, this.ws);
            this.manuallyClosed = false;
        });
        this.wsCheckTicker = new Ticker(this.ctx, this.heartbeatInterval);
        this.wsCheckTicker.onTick.subscribe(() => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e;
            const receiveTimeRange = Monitor.performanceNow() - this.wsLastReceiveTime + this.heartbeatInterval / 1.5; // bias
            if ((yield this.net.getConnectionStatus()) === ConnectionStatus.Disconnected) {
                Logger.ctxDebug(this.ctx, 'network disconnected, skip ws check');
                return;
            }
            if (!((_a = this.ws) === null || _a === void 0 ? void 0 : _a.isOpen())) {
                Logger.ctxDebug(this.ctx, 'ws ticker try connect if closed');
                this.resolve(ServiceIdentity.Monitor).emitMetrics(MonitorItem.WebSocketConnectAfterClose, {
                    count: 1
                }, {
                    url: (_c = (_b = this.ws) === null || _b === void 0 ? void 0 : _b.url) !== null && _c !== void 0 ? _c : 'unknown'
                });
                try {
                    yield this.connectWs(true);
                }
                catch (err) {
                    Logger.ctxWarn(this.ctx, 'ws ticker try connect err:', err);
                }
            }
            else if (receiveTimeRange > this.maxEmptyWindow) {
                Logger.ctxDebug(this.ctx, 'not receive any packet in window, ws close connection');
                this.resolve(ServiceIdentity.Monitor).emitMetrics(MonitorItem.WebSocketConnectNoHeartbeat, {
                    count: 1
                }, {
                    url: (_e = (_d = this.ws) === null || _d === void 0 ? void 0 : _d.url) !== null && _e !== void 0 ? _e : 'unknown'
                });
                try {
                    yield this.connectWs(true);
                }
                catch (err) {
                    Logger.ctxWarn(this.ctx, 'ws ticker window connect err:', err);
                }
            }
            else if (receiveTimeRange > this.heartbeatInterval) {
                // try to send a heartbeat to reset receive timer
                this.sendWsHeartbeat();
            }
        }));
    }
    closeWs(isAuto) {
        var _a, _b;
        if (this.ctx.option.webSocketLevel === WebSocketLevel.Disable) {
            return;
        }
        (_a = this.ws) === null || _a === void 0 ? void 0 : _a.close();
        // 如果是通过手动调用的方式断开ws(isAuto是undefined), 那么不重连
        this.manuallyClosed = !isAuto;
        // stop ticker to prevent useless check
        if (this.manuallyClosed) {
            (_b = this.wsCheckTicker) === null || _b === void 0 ? void 0 : _b.stop();
        }
    }
    connectWs(isAuto) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.ctx.option.webSocketLevel === WebSocketLevel.Disable) {
                return;
            }
            if (this.manuallyClosed && isAuto) {
                Logger.ctxDebug(this.ctx, 'ignore connect since manually closed');
                return;
            }
            this.closeWs(true);
            const startTime = Monitor.performanceNow();
            try {
                yield this.ws.performOpen();
                this.resolve(ServiceIdentity.Monitor).emitMetrics(MonitorItem.WsConnect, {
                    ws_cost: Monitor.performanceNow() - startTime
                }, {
                    error_code: '0',
                    url: (_a = this.ws.option.frontierUrl) !== null && _a !== void 0 ? _a : 'unknown'
                });
                this.resetWsHeartbeat();
                (_b = this.wsCheckTicker) === null || _b === void 0 ? void 0 : _b.start();
            }
            catch (err) {
                this.resolve(ServiceIdentity.Monitor).emitMetrics(MonitorItem.WsConnect, {
                    ws_cost: Monitor.performanceNow() - startTime
                }, {
                    error_code: '1',
                    url: (_c = this.ws.option.frontierUrl) !== null && _c !== void 0 ? _c : 'unknown'
                });
                throw err;
            }
        });
    }
    sendWsHeartbeat() {
        var _a;
        if ((_a = this.ws) === null || _a === void 0 ? void 0 : _a.isOpen()) {
            this.ws.send('hi');
        }
        else {
            Logger.ctxWarn(this.ctx, 'ws not connect but try to send heartbeat');
        }
    }
    resetWsHeartbeat() {
        this.wsLastReceiveTime = Monitor.performanceNow();
    }
}

export { NetworkManager };
//# sourceMappingURL=NetworkManager.js.map
